/* saif-select.js — SAIF_SELECT_V1 (2026-08-02)
 *
 * Ties the course-card tick boxes to the one selection bar, and turns the bar's
 * two controls into the two cart routes.
 *
 * The routes are NOT symmetric, and the asymmetry is the whole design:
 *
 *   pay in full     1 tick  -> that course
 *                   2 ticks -> the matching Build Your Path pair as ONE line. Until
 *                              2026-08-05 this added two SEPARATE lines and let the
 *                              automatic -$9.99 discount reconcile them; the owner
 *                              deactivated that discount, so separate lines became
 *                              MORE expensive than the bundle the bar was quoting and
 *                              the cart had to merge and reload. See
 *                              SAIF_PAIR_AT_CHECKOUT_V1.
 *                   3 ticks -> the Complete Recitation Path bundle as ONE line,
 *                              so the charge equals the quote to the cent
 *
 *   payment plan    every count rides a BUNDLE product, because a selling plan
 *                   attaches to a variant and cannot span a cart. Two courses on
 *                   two separate plans would be two contracts at full price.
 *
 * NO ARITHMETIC HAPPENS HERE. Every price is a pre-rendered, already-converted
 * money string from Liquid. The store serves 107 currencies and Shopify
 * ceiling-rounds conversions, so a number divided in the browser is wrong in
 * most of them.
 */
(function () {
  'use strict';

  if (window.__saifSelect) return;
  window.__saifSelect = true;

  var bar = document.querySelector('[data-select-bar]');
  var dataEl = document.querySelector('[data-select-bar-data]');
  if (!bar || !dataEl) return;

  var DATA;
  try {
    DATA = JSON.parse(dataEl.textContent);
  } catch (e) {
    /* Malformed data means every price would be undefined. Leave the bar hidden
       and let the cards stand alone rather than show "$undefined". */
    return;
  }
  if (!DATA || !DATA.counts) return;

  var cards = [].slice.call(document.querySelectorAll('[data-select-card]'));

  /* SINGLE MODE — a course page has no tick boxes; the selection is permanently one
     course. Everything downstream reads count(), so the rest of the file is unchanged. */
  var FIXED = Number(bar.getAttribute('data-fixed-count')) || 0;
  if (!cards.length && !FIXED) return;

  var grantsEl = bar.querySelector('[data-bar-grants]');
  var courseEl = bar.querySelector('[data-bar-course]');
  var offerEl = bar.querySelector('[data-bar-offer]');
  var totalEl = bar.querySelector('[data-bar-total]');

  /* SAIF_BAR_EXACT_V1 (2026-08-04) — the pay-in-full price for TWO ticks, as the cart will
     actually charge it.

     Two ticks adds the two singles and lets Shopify's automatic $9.99 discount do the
     arithmetic. Shopify ceiling-rounds a converted PRODUCT price to a whole unit but converts
     a fixed DISCOUNT at the raw rate, so the Build Your Path product price the bar was quoting
     can never equal that sum outside the base currency — measured at $158.00 quoted against
     $157.69 charged in CAD.

     saif-price-exact.js already reproduces Shopify's own arithmetic for the homepage; this
     reuses it rather than re-deriving it. In the base currency rate is 1, the discount converts
     to exactly 999 and this returns the same number Liquid rendered, so nothing moves.

     Fails open in every direction: no script, no data, an implausible result, or any count
     other than 2, and the server-rendered price stands. */
  function exactTotal(n) {
    /* ⛔ RETIRED 2026-08-05 — there is no discount left to correct for.
       This existed only because the automatic discount "Build Your Path - Any 2 Courses" made
       two singles cost slightly less than the Build Your Path price, and Shopify converts a
       fixed discount at the raw rate while ceiling-rounding a product price. The owner
       deactivated that discount, and two ticks now buy the Build Your Path PRODUCT outright
       (SAIF_PAIR_AT_CHECKOUT_V1 below), so the correction started advertising a price nobody
       is charged: the bar quoted $157.72 against a cart of $158.00 — measured, not guessed.

       Returning '' makes the caller keep Liquid's server-rendered figure, which is the bundle's
       real price in the visitor's own currency. The body is left intact below so this can be
       revived verbatim if those discounts ever come back. */
    return '';
    /* eslint-disable no-unreachable */
    if (n !== 2) return '';
    var P = window.SaifPrice;
    var ex = DATA.exact;
    if (!P || typeof P.exact !== 'function' || typeof P.money !== 'function') return '';
    if (!ex || !(ex.unitCents > 0) || !(ex.d2 > 0)) return '';
    var cents = P.exact(ex.unitCents * 2, ex.d2);
    /* A total must be positive and below the undiscounted pair, or the numbers have stopped
       making sense and Liquid's figure is the safer one. */
    if (!(cents > 0 && cents < ex.unitCents * 2)) return '';
    return P.money(cents);
  }

  /* The per-course figure for two ticks, restated from the corrected total so the row cannot
     read "$79.00 each · ... " above a $157.69 price. Same fail-open rules as exactTotal. */
  function exactEach(n) {
    /* ⛔ RETIRED with exactTotal above, and for the same reason: no discount, nothing to
       correct. Left intact below so it can be revived verbatim. */
    return '';
    /* eslint-disable no-unreachable */
    if (n !== 2) return '';
    var P = window.SaifPrice;
    var ex = DATA.exact;
    if (!P || typeof P.exact !== 'function' || typeof P.money !== 'function') return '';
    if (!ex || !(ex.unitCents > 0) || !(ex.d2 > 0) || !DATA.eachTpl) return '';
    var cents = P.exact(ex.unitCents * 2, ex.d2);
    if (!(cents > 0 && cents < ex.unitCents * 2)) return '';
    return String(DATA.eachTpl).replace('__AMT__', P.money(Math.round(cents / 2)));
  }

  var wasEl = bar.querySelector('[data-bar-was]');
  var offEl = bar.querySelector('[data-bar-off]');
  var ctaEl = bar.querySelector('[data-bar-cta]');
  var payEl = bar.querySelector('[data-bar-pay]');
  var segEl = bar.querySelector('[data-bar-seg]');
  var noteEl = bar.querySelector('[data-bar-segnote]');
  var ctaLabel = ctaEl ? ctaEl.textContent : 'Checkout';
  var busy = false;

  /* Every guarantee statement on this page that is true only for a full-price purchase.
     Collected once at load — none of these are created later. */
  var guarantees = [].slice.call(document.querySelectorAll('[data-saif-fullprice-note]'));

  /* How the shopper has chosen to pay, held as a PAYMENT COUNT (0 = in full) rather
     than an index. Ticking another course rebuilds the options, and an index would
     then point at a different plan; a count survives because every product carries
     the same 2- and 4-payment plans. */
  var payN = 0;

  function planFor(d, n) {
    return (d.plans || []).filter(function (p) { return p.n === n; })[0] || null;
  }

  /* saif-plans.js reveals [data-saif-plans] only in plan-eligible countries. It may
     run before or after this file, so the plans row's own hidden state is left
     entirely to that script — this code only ever writes its CONTENTS. */

  function selected() {
    return cards.filter(function (c) {
      var i = c.querySelector('[data-select-input]');
      return i && i.checked;
    });
  }

  /* How many courses are being bought. Ticks on the collection; fixed on a course page. */
  function count() {
    return FIXED || selected().length;
  }

  /* ── SAIF_PLAN_TARGET_V1 ────────────────────────────────────────────────────────────
     Which single variant a payment plan should buy for THIS selection.

     A selling plan attaches to a variant, so a multi-course plan purchase has to be one
     bundle product — and it must be the bundle that contains the courses actually ticked.
     Matching a pair is done on the Build Your Path variant TITLE, which names both courses;
     this is the same rule saif-plans.js:targetFor() already applies on the cart, kept
     deliberately identical so the two can never disagree about the same selection.

     Returns 0 when nothing matches, and the caller must then refuse to check out rather than
     fall back to a fixed variant — falling back is what shipped the wrong course. */
  var PLAN_WORDS = {
    'foundation-of-recitation': 'foundation',
    'precision-of-recitation': 'precision',
    'mastery-of-recitation': 'mastery'
  };

  function handleOf(card) {
    var i = card.querySelector('[data-select-input]');
    return (i && i.getAttribute('data-select-handle')) || '';
  }

  function planTarget(n, picked) {
    if (FIXED) return (DATA.counts[String(n)] || {}).variant || 0;
    if (n === 1) {
      /* The card knows its own variant — the same attribute the pay-in-full path reads,
         which is why paying in full was never wrong. */
      return (picked[0] && picked[0].getAttribute('data-select-variant')) || 0;
    }
    if (n >= 3) return DATA.crpVariant || (DATA.counts['3'] || {}).variant || 0;

    var words = picked.map(function (c) { return PLAN_WORDS[handleOf(c)]; }).filter(Boolean);
    if (words.length !== 2) return 0;
    var vs = DATA.bypVariants || [];
    for (var i = 0; i < vs.length; i++) {
      var t = String(vs[i].title || '');
      if (words.every(function (w) { return t.indexOf(w) !== -1; })) return vs[i].id;
    }
    return 0;
  }

  /* Set text and show, or hide entirely. Not `textContent = ''`: an empty <s> still
     paints its strikethrough and an empty pill still holds its padding, so the row
     keeps a gap where nothing is. */
  function flag(el, text) {
    if (!el) return;
    if (text) { el.textContent = text; el.removeAttribute('hidden'); }
    else { el.textContent = ''; el.setAttribute('hidden', ''); }
  }

  function paint() {
    var n = count();

    cards.forEach(function (c) {
      var i = c.querySelector('[data-select-input]');
      c.classList.toggle('is-selected', !!(i && i.checked));
    });

    document.body.classList.add('saif-select-active');

    bar.classList.toggle('saif-select-bar--empty', n === 0);
    if (!n) { measure(); return; }

    var d = DATA.counts[String(n)];
    if (!d) return;

    /* At one course "$59.99 each" beside "$59.99" is the same number twice, so the
       per-course figure only joins the line from two up — and the separator is built
       with it, never left stranded. */
    var eachTxt = exactEach(n) || d.each;

    /* SAIF_BAR_NAME_V1 (2026-08-23) - say WHICH course this is.
       A customer bought Advanced believing it was Foundation, and nothing on this page
       contradicted her: the cards are above the fold, all three courses cost the same, and this
       bar listed the price, the discount, the Studio months, the review credits, three payment
       options and a guarantee without ever naming the course. Order #1060.

       One course gets its full name; two get first words so the line stays short; three is a
       phrase rather than a list. The name leads because it is the thing being bought. */
    var picked = selected();
    var label = '';
    if (!FIXED) {
      if (n === 1) {
        label = picked[0] && picked[0].getAttribute('data-course-name') || '';
      } else if (n === 2) {
        label = picked.map(function (c) {
          return (c.getAttribute('data-course-name') || '').split(' ')[0];
        }).filter(Boolean).join(' + ');
      } else if (n >= 3) {
        label = 'All three courses';
      }
    }
    if (courseEl) courseEl.textContent = label;
    var offerTxt = (n > 1 && eachTxt ? eachTxt + ' · ' : '') + (d.grants || '');
    if (offerEl) offerEl.textContent = offerTxt;
    else if (grantsEl) grantsEl.textContent = (label ? label + ' · ' : '') + offerTxt;
    if (ctaEl) ctaEl.textContent = ctaLabel;

    /* A plan the new count does not offer cannot stay selected. */
    var chosen = payN ? planFor(d, payN) : null;
    if (payN && !chosen) payN = 0;

    /* THE number. Paying in full it is the total; on a plan it is the monthly
       figure, in the same serif at the same size — that is the whole point of this
       control.

       The struck price and the % off STAY on a plan (owner, 2026-08-02: "keep this
       same percentage off for the monthly plans too"). They describe the course, not
       the instalment: the course is 40% off whichever way you pay, and the plan is a
       separate financing choice on top. The note directly beneath always spells out
       the real total ("x 2 monthly · $69.98 total · first payment today"), so the
       full cost is never hidden behind the badge. */
    if (totalEl) totalEl.textContent = chosen ? chosen.amount : (exactTotal(n) || d.total || '');
    flag(wasEl, d.was);
    flag(offEl, d.off);

    if (segEl) {
      var plans = d.plans || [];
      if (!plans.length) {
        segEl.innerHTML = '';
        if (noteEl) noteEl.textContent = '';
      } else {
        var btns = ['<button type="button" data-pay-n="0" aria-pressed="' + (payN === 0) + '">' +
          (DATA.segFull || 'Pay in full') + '</button>'];
        plans.forEach(function (p) {
          btns.push('<button type="button" data-pay-n="' + p.n + '" aria-pressed="' + (payN === p.n) + '">' + p.seg + '</button>');
        });
        segEl.innerHTML = btns.join('');
        /* The note carries the plan's TOTAL. A per-payment figure on its own hides a
           premium the rest of the site is careful never to hide.

           It also carries the 30-day guarantee on the pay-in-full note and NOT on a plan
           note, so the guarantee appears and disappears with the choice at zero extra
           height — the bar states it without growing a line. */
        if (noteEl) noteEl.textContent = chosen ? chosen.note : (DATA.segNoteFull || '');
      }
    }

    /* SAIF_GUARANTEE_V2 (owner, 2026-08-02): "keep the 30 day in as many places as
       possible, but the moment they select a payment plan it all disappears."

       Plans are non-refundable, so every guarantee statement on a BUYING page has to go
       when one is chosen. The old plan picker did this; the segmented control that replaced
       it did not, which left plan buyers reading a money-back promise that did not apply to
       them — a regression I introduced when the picker was removed.

       ⚠️ Scoped to [data-saif-fullprice-note]. The HOMEPAGE guarantee section deliberately
       carries no such marker: there is no pay control there, nothing to select, and the
       owner wants it stated permanently. Do not tag it. */
    guarantees.forEach(function (el) {
      if (chosen) el.setAttribute('hidden', '');
      else el.removeAttribute('hidden');
    });

    measure();
  }

  /* The bar is fixed, so the document has to end above it. Measured rather than
     guessed: the grants line wraps to two lines in several currencies and in
     Arabic, and a hardcoded pad leaves the last card unreachable when it does. */
  function measure() {
    var h = bar.offsetHeight;
    if (h) document.documentElement.style.setProperty('--saif-select-bar-h', (h + 16) + 'px');
  }

  /* ── ticking ──────────────────────────────────────────────────────────
     The input is the source of truth; the card-wide tap is a convenience for
     pointers, which is why it dispatches a real change event rather than
     setting state directly. A card that only toggles from a 26px box is a card
     most people will tap and think is broken. */
  cards.forEach(function (card) {
    var input = card.querySelector('[data-select-input]');
    if (!input) return;

    input.addEventListener('change', function () {
      paint();
      if (window.SaifTrack) {
        window.SaifTrack('course_select', {
          course: card.getAttribute('data-course-handle'),
          on: input.checked,
          count: selected().length
        });
      }
    });

    card.addEventListener('click', function (e) {
      /* The label already toggles the input natively — handling it here too
         would toggle twice and land back where it started. */
      if (e.target.closest('.saif-course-card__tick')) return;
      /* The two real controls inside the card keep their own behaviour. */
      if (e.target.closest('[data-card-listen]')) return;
      if (e.target.closest('a')) return;
      input.checked = !input.checked;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });

  /* ── the two cart routes ─────────────────────────────────────────────── */

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var CART_URL = ROOT + 'cart';
  var OURS = DATA.ours || [];

  /* ⚠️ THIS USED TO SWALLOW EVERY HTTP FAILURE (owner hit it 2026-08-03: "clicked checkout
     and it added nothing to cart first time, worked after").

     It read only the body and ignored `r.ok`. Shopify's own errors arrive as JSON
     {status, message} and every caller keys off `.status` — but a 429 from the cart rate
     limit, a 5xx, or a Cloudflare challenge is NOT JSON. `r.json()` threw, the catch returned
     null, and null reads as SUCCESS: the buyer was sent to an empty cart with no explanation,
     having clicked the one button that matters. Retrying later worked because the rate-limit
     window had passed.

     Now an HTTP failure is reported in the same {status} shape the callers already understand,
     and a 429 — the transient one — gets a single retry before giving up. */
  var post = function (path, body, _retried) {
    return fetch(ROOT + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().catch(function () { return null; }).then(function (j) {
        if (r.ok) return j;
        if (r.status === 429 && !_retried) {
          return new Promise(function (res) { setTimeout(res, 1200); })
            .then(function () { return post(path, body, true); });
        }
        return (j && j.status) ? j : { status: r.status, message: 'http_' + r.status };
      });
    }).catch(function () { return { status: 0, message: 'network' }; });
  };

  /* A line's full identity for this purpose: which variant, on which plan. Two
     lines of the same course, one on a plan and one not, are NOT interchangeable. */
  function idOf(variantId, planId) { return String(variantId) + '|' + (planId || ''); }
  function lineId(it) {
    return idOf(it.variant_id, it.selling_plan_allocation && it.selling_plan_allocation.selling_plan.id);
  }

  /* ── settle the cart BEFORE navigating ────────────────────────────────
     The cart page used to receive whatever was added, notice the overlap, strip
     the duplicates and reload — so the buyer watched their cart repair itself
     through three reloads (owner, 2026-08-02: "I dont like this refreshing
     stuff"). The bar already knows the final shape, so it computes it here and
     applies it in ONE /cart/update.js, which both removes and adds. The cart
     page then loads once, correct, and its normaliser finds nothing to do.

     Only handles in DATA.ours are ever touched, and any line carrying _gift or
     _booking_data is left alone whatever its handle: a gifted course is a
     legitimate second line, and a booking line holds the reservation payload
     that the worker needs to hold a slot. */
  function settle(want) {
    /* ⚠️ SINGLE MODE ADDS, IT NEVER SETTLES.
       Settling means "make the cart equal this selection", which is right on the
       collection because the ticks ARE the whole selection. On a course page the
       visitor is adding one course to whatever they already hold — settling there
       would delete a bundle they deliberately bought, silently, on their way to the
       cart. The cart's own normaliser converges any real overlap. */
    /* ⚠️ SINGLE MODE ADDS, THEN CONVERGES — it still never settles.
       Adding stays right: the visitor is adding one course to whatever they already hold.
       But leaving it at that meant two singles reached the cart separately and the cart page
       merged them after paint, which the owner saw as a refresh (2026-08-05: "make it like the
       others where it combines before it shows in cart"). SaifConvergeCart (saif-course-merge.js)
       collapses an overlap into the cheaper bundle here, so the cart loads once, correct.

       It only ever REPLACES lines a bundle covers, never deletes a bundle the visitor chose, and
       it declines whenever the bundle is not strictly cheaper. If the script is absent — any page
       that does not load it — this is exactly the old behaviour. */
    if (FIXED) {
      return post('cart/add.js', { items: want }).then(function (res) {
        if (typeof window.SaifConvergeCart !== 'function') return res;
        return window.SaifConvergeCart().then(function () { return res; }, function () { return res; });
      });
    }

    return fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart || !cart.items) return post('cart/add.js', { items: want });   // fail open

        var updates = {};
        var satisfied = {};
        var wantIds = {};
        want.forEach(function (w) { wantIds[idOf(w.id, w.selling_plan)] = w; });

        /* Which VARIANTS are being removed by line key. Needed because a line key and a
           variant id can name the same product, and /cart/update.js cannot be asked to do
           both to it at once — see SAIF_SETTLE_SAME_VARIANT_V1 below. */
        var removingVariants = {};

        cart.items.forEach(function (it) {
          if (OURS.indexOf(it.handle) === -1) return;
          var p = it.properties || {};
          if (p._gift || p._booking_data) return;
          var id = lineId(it);
          if (wantIds[id] && !satisfied[id]) {
            satisfied[id] = true;
            /* already there — only correct a quantity that drifted above one */
            if (it.quantity !== 1) updates[it.key] = 1;
          } else {
            updates[it.key] = 0;      // a course the bundle covers, or a stale bundle
            removingVariants[String(it.variant_id)] = true;
          }
        });

        var toAdd = want.filter(function (w) { return !satisfied[idOf(w.id, w.selling_plan)]; });
        var plain = toAdd.filter(function (w) { return !w.selling_plan; });
        var planned = toAdd.filter(function (w) { return w.selling_plan; });

        /* ⚠️ SAIF_SETTLE_SAME_VARIANT_V1 (owner-reported, 2026-08-04) — THE EMPTY CART.

           Reproduced: pick Foundation on 4 monthly and check out, go back, switch to pay in
           full, check out again — and the cart is EMPTY.

           A planned line and a plain line are different identities to us (`...|9999974719` vs
           `...|`) but the SAME VARIANT to Shopify. So this built one update.js saying
           "line key X -> 0" and "variant 52700453110079 -> 1", which are the same product, and
           Shopify resolved them against each other. Nothing survived.

           /cart/update.js genuinely does take keys and variant ids together — that part of the
           original comment is right — but not when they refer to the same variant. When they do,
           the removal has to land first, on its own, and the addition follows. Only that case
           pays the extra round trip; everything else still rides one request. */
        var collides = plain.some(function (w) { return removingVariants[String(w.id)]; });

        if (!collides) plain.forEach(function (w) { updates[w.id] = 1; });

        var first = Object.keys(updates).length
          ? post('cart/update.js', { updates: updates })
          : Promise.resolve(cart);

        return first.then(function (res) {
          /* If update.js refused the mixed object, fall back to a plain add and
             let the cart page normalise — worse UX, never a wrong cart. */
          if (res && res.status && plain.length && !collides) return post('cart/add.js', { items: plain });
          return res;
        }).then(function (res) {
          if (res && res.status) return res;
          /* The collision case: the removal is done, now add the plain line for real. */
          if (collides && plain.length) return post('cart/add.js', { items: plain });
          return res;
        }).then(function (res) {
          /* ⚠️ PROPAGATE. This step used to ignore `res` entirely and return null, so a
             failed first call still reported success and go() navigated to an empty cart. */
          if (res && res.status) return res;
          if (!planned.length) return null;
          return post('cart/add.js', { items: planned });
        });
      });
  }

  function go(btn, label, items, evt) {
    if (busy) return;
    busy = true;
    var restore = btn.innerHTML;
    btn.disabled = true;
    btn.textContent = label;

    /* Fired BEFORE the add, deliberately: a failed add is still buy intent, and
       counting only successes would flatter the page. */
    if (window.SaifTrack) window.SaifTrack(evt.name, evt.props);

    /* A click that appears to do nothing is worse than an error: the buyer assumes the site
       is broken and leaves. Say so on the button itself, then hand it back. */
    var failed = function () {
      busy = false;
      btn.disabled = false;
      btn.textContent = (ctaEl && ctaEl.getAttribute('data-retry')) || 'Try again';
      setTimeout(function () { btn.innerHTML = restore; }, 2200);
    };

    settle(items)
      .then(function (resp) {
        if (resp && resp.status) {          // Shopify errors AND now HTTP failures
          if (window.SaifTrack) {
            window.SaifTrack('select_checkout_failed', { status: resp.status, route: evt.props.route });
          }
          failed();
          return;
        }
        /* To the CART, not straight to /checkout: jumping to checkout hits the
           account wall mid-add and strands logged-out buyers. */
        window.location.href = CART_URL;
      })
      .catch(function () {
        if (window.SaifTrack) window.SaifTrack('select_checkout_failed', { status: 'threw', route: evt.props.route });
        failed();
      });
  }

  if (ctaEl) {
    ctaEl.addEventListener('click', function () {
      var picked = selected();
      var n = count();
      if (!n) return;
      var d = DATA.counts[String(n)];
      if (!d) return;

      /* Checkout follows the segmented control — the whole reason the control
         replaced separate plan buttons. Three routes, and they are not symmetric:

           on a plan   → the BUNDLE for this count, carrying the selling plan. A plan
                         attaches to a variant and cannot span a cart, so two courses
                         on a plan must be one bundle product, never two lines.
           3, in full  → the bundle as one line, so the charge equals the quote to
                         the cent (three singles come to $149.99, the bundle $150.00).
           2, in full  → the matching Build Your Path pair as ONE line. This used to be two
                         separate lines because "the live automatic discount does the
                         arithmetic" — that discount was deactivated 2026-08-05, so separate
                         lines now cost more than the bundle the bar is quoting. See
                         SAIF_PAIR_AT_CHECKOUT_V1 below.
           1, in full  → the single course, obviously. */
      var chosen = payN ? planFor(d, payN) : null;
      var items, route;
      if (chosen) {
        /* ⛔ SAIF_PLAN_TARGET_V1 — the variant must match WHAT WAS TICKED, not how many.
           This used to send `d.variant`, which is keyed by count alone, so Precision + Mastery
           on a plan delivered Foundation + Precision: right money, wrong course, and course
           access is granted from the order line so the buyer lost what they paid for.

           A plan attaches to a variant, so there is exactly one correct product per selection
           and all of them already exist at the same price. Plan ids are shared across all five
           products, so `chosen.id` stays valid whichever variant we name. */
        var target = planTarget(n, picked);
        if (!target) {
          /* Refuse rather than charge for the wrong course. Reaching here means the catalogue
             changed under us (a renamed Build Your Path variant, a removed product) — sending
             the old fixed variant is precisely the bug this replaced. */
          if (window.SaifTrack) {
            window.SaifTrack('select_plan_target_missing', {
              count: n, payments: chosen.n,
              handles: picked.map(handleOf).join(',')
            });
          }
          return;
        }
        items = [{ id: target, quantity: 1, selling_plan: chosen.id }];
        route = 'plan' + chosen.n;
      } else if (n === 3 || FIXED) {
        /* Single mode has no cards to read variants from, and its d.variant IS the
           one course — so the same branch serves both. */
        items = [{ id: d.variant, quantity: 1 }];
        route = FIXED ? 'single' : 'bundle';
      } else if (n === 2 && !FIXED) {
        /* SAIF_PAIR_AT_CHECKOUT_V1 (owner, 2026-08-05: "i want it to merge before hand so when
           it takes them to cart its already a bundle").

           Two courses paid in full used to be added as two SEPARATE lines. The reason is in the
           comment above — "at two the live automatic discount does the arithmetic" — and that
           reason DIED on 2026-08-05 when the owner deactivated "Build Your Path - Any 2 Courses".

           Two things were wrong once it died, not one:
             · the cart page merged them on load and RELOADED, which is the refresh he reported
             · worse, THE BAR ALREADY QUOTES THE BUNDLE. DATA.counts['2'].total is Build Your
               Path's price, so the bar promised 158.00 and the cart charged 172.00 until the
               merge caught up. The quote and the charge disagreed in between.

           Sending the pair variant fixes both: the cart matches the quote to the cent and loads
           once, already a bundle. planTarget() resolves the variant from WHAT WAS TICKED, not
           from the count — the same rule the plan route uses, and the same one that stopped
           Precision + Mastery being delivered as Foundation + Precision. */
        var pairTarget = planTarget(n, picked);
        if (pairTarget) {
          items = [{ id: pairTarget, quantity: 1 }];
          route = 'bundle2';
        } else {
          /* Fail open to the old behaviour rather than refuse the sale: the cart's own
             normalizer still converges it, with the reload. A renamed Build Your Path variant
             lands here. */
          if (window.SaifTrack) {
            window.SaifTrack('select_pair_target_missing', { handles: picked.map(handleOf).join(',') });
          }
          items = picked.map(function (c) {
            return { id: c.getAttribute('data-select-variant'), quantity: 1 };
          });
          route = 'singles';
        }
      } else {
        items = picked.map(function (c) {
          return { id: c.getAttribute('data-select-variant'), quantity: 1 };
        });
        route = 'singles';
      }

      go(ctaEl, ctaEl.getAttribute('data-adding') || 'Adding…', items, {
        name: 'select_checkout',
        props: { count: n, route: route, payments: chosen ? chosen.n : 0 }
      });
    });
  }

  /* Delegated: the segments are rebuilt from scratch on every tick, so a listener
     bound to them directly would be thrown away the first time anything changes. */
  if (segEl) {
    segEl.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-pay-n]');
      if (!btn) return;
      payN = Number(btn.getAttribute('data-pay-n')) || 0;
      paint();
      if (window.SaifTrack) {
        window.SaifTrack('select_pay_choice', { count: selected().length, payments: payN });
      }
    });
  }

  /* saif-plans.js may reveal the pay control after first paint; re-measure when it
     does, or the page's bottom padding stays two lines short. */
  if (payEl && window.MutationObserver) {
    new MutationObserver(function () { measure(); presetLongestPlan(); })
      .observe(payEl, { attributes: true, attributeFilter: ['hidden'] });
  }
  window.addEventListener('resize', measure);

  /* bfcache restore (Back from cart or login) resurrects the page with the
     buttons frozen mid-add. */
  window.addEventListener('pageshow', function (ev) {
    if (!ev.persisted) return;
    busy = false;
    if (ctaEl) { ctaEl.disabled = false; }
    paint();
  });

  /* SAIF_SELECT_START_V1 (owner, 2026-08-03) — ?start=1 opens the page on the MINIMUM
     selection instead of all three.

     ⚠️ As of SAIF_SELECT_DEFAULT_V2 (owner, 2026-08-18) the page opens on Foundation alone by
     DEFAULT, so this block is now a no-op on the ordinary catalog — it is kept because it still
     guarantees the minimum for the homepage's "$19.99" link regardless of what the default
     later becomes, and because removing it would silently change that link's behaviour if the
     default is ever put back. It must not be read as evidence that all three still start ticked.

     Untick from the END, so the one left standing is Foundation — the level the $19.99 refers
     to and the only sane single course to open on. Fired before paint() so the bar never
     renders the three-course price first and then jumps. */
  var wantsMinimum = false;
  try { wantsMinimum = new URLSearchParams(location.search).get('start') === '1'; } catch (e) {}

  (function () {
    if (!wantsMinimum || FIXED || !cards.length) return;
    for (var i = cards.length - 1; i > 0; i--) {
      var input = cards[i].querySelector('[data-select-input]');
      if (input) input.checked = false;
      cards[i].classList.remove('is-selected');
    }
    if (window.SaifTrack) window.SaifTrack('course_select_preset', { preset: 'minimum', count: 1 });
  })();

  /* SAIF_QUIZ_SELECT_V1 (owner, 2026-08-10) — ?select=<handle> opens the page with exactly
     that course ticked. Only the quiz result carries it: the card has just recommended ONE
     course by name, so landing on the catalog with all three ticked contradicted the
     recommendation the visitor clicked — and the button's single-course price never matched
     the bar's three-course total. Runs AFTER ?start=1 on purpose: a link carrying both means
     the quiz sent it, and the recommendation wins.

     An unknown handle changes nothing: a mistyped param must degrade to the ordinary catalog,
     never to an empty selection. */
  (function () {
    var wanted = null;
    try { wanted = new URLSearchParams(location.search).get('select'); } catch (e) {}
    if (!wanted || FIXED || !cards.length) return;
    var match = null;
    cards.forEach(function (c) {
      var h = (c.getAttribute('data-course-handle') || handleOf(c) || '').toLowerCase();
      if (h === wanted.toLowerCase()) match = c;
    });
    if (!match) return;
    cards.forEach(function (c) {
      var input = c.querySelector('[data-select-input]');
      if (!input) return;
      input.checked = (c === match);
      c.classList.toggle('is-selected', c === match);
    });
    /* course_select_preset, NOT course_select — a machine ticking a box is not a person
       choosing a course, and mixing them would corrupt the choice data. */
    if (window.SaifTrack) window.SaifTrack('course_select_preset',
      { preset: 'quiz', course: wanted, count: 1 });
    paint();
  })();

  /* SAIF_SELECT_DEFAULT_V2 (owner, 2026-08-18) — never open on an empty bar.

     The default tick now lives on Foundation alone, and Foundation is not tickable for anyone
     who already owns it. Without this, a returning customer — the person most likely to buy a
     second course — would land on nothing selected and a bar with no price in it, where before
     they landed on the two they had not bought. Ticks the first card that CAN be ticked.

     Runs last, and only when the selection is genuinely empty, so it can never overrule
     ?select= or ?start=. Init only: unticking everything by hand stays a legitimate state, and
     re-ticking a box under someone's finger would be worse than an empty bar. */
  (function () {
    if (FIXED || !cards.length || selected().length) return;
    for (var i = 0; i < cards.length; i++) {
      var input = cards[i].querySelector('[data-select-input]');
      if (!input) continue;                       /* enrolled/sold-out cards have no tick */
      input.checked = true;
      cards[i].classList.add('is-selected');
      if (window.SaifTrack) window.SaifTrack('course_select_preset',
        { preset: 'first-available', course: handleOf(cards[i]), count: 1 });
      break;
    }
  })();

  /* ...and the LONGEST payment plan, where the visitor is allowed one (owner, 2026-08-03:
     "make it minimum as in 4 monthly if they have access to it").

     This is what makes the page match the link that opened it. The homepage says "start your
     journey with Saif today for $19.99", and $19.99 is not a course price — it is Foundation
     over four payments. Landing on $59.99 would show a number the sentence never mentioned.

     Payment plans are GEO-GATED: saif-plans.js reveals [data-saif-plans] only after the
     worker confirms the country is allowed, which happens after this script runs. So the
     choice cannot be made now — it is made the moment the control appears, and never at all
     for a visitor who is not offered plans. They correctly land on pay-in-full at $59.99,
     which is genuinely their minimum. */
  function presetLongestPlan() {
    if (!wantsMinimum || payN !== 0 || !payEl || payEl.hidden) return;
    var d = DATA.counts[String(count())];
    var plans = (d && d.plans) || [];
    if (!plans.length) return;
    payN = plans.reduce(function (m, p) { return p.n > m ? p.n : m; }, 0);
    wantsMinimum = false;   /* one-shot: never fight a later manual choice */
    paint();
    if (window.SaifTrack) window.SaifTrack('course_select_preset', { preset: 'minimum_plan', payments: payN });
  }

  paint();
  presetLongestPlan();   /* in case the geo gate had already opened before we loaded */
})();
